import React from "react";
import { MEDIA_TYPES, STATUS_TYPES } from "./FilterOptions";

interface FilterPopupProps {
    open: boolean;
    onClose: (filters: { mediaType: string; status: string[] }) => void;
    anchorRef: React.RefObject<HTMLDivElement>;
    mediaType: string;
    status: string[];
}

const FilterPopup: React.FC<FilterPopupProps> = ({ open, onClose, anchorRef, mediaType, status }) => {
    const [position, setPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const [selectedMediaType, setSelectedMediaType] = React.useState<string>(mediaType);
    const [selectedStatus, setSelectedStatus] = React.useState<string[]>(status);
    const [statusDropdownOpen, setStatusDropdownOpen] = React.useState(false);
    const statusDropdownRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (open && anchorRef.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX
            });
        }
    }, [open, anchorRef]);

    React.useEffect(() => {
        setSelectedMediaType(mediaType);
    }, [mediaType, open]);

    React.useEffect(() => {
        setSelectedStatus(status);
    }, [status, open]);

    // Close dropdown when clicking outside
    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
                setStatusDropdownOpen(false);
            }
        }
        if (statusDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [statusDropdownOpen]);

    const ALL_OPTION = { label: "All", value: "all" };
    const STATUS_OPTIONS = [ALL_OPTION, ...STATUS_TYPES];

    const handleStatusToggle = (value: string) => {
        if (value === "all") {
            setSelectedStatus(["all"]);
        } else {
            setSelectedStatus(prev => {
                const newSelection = prev.includes(value) ? prev.filter(v => v !== value) : [...prev.filter(v => v !== "all"), value];
                return newSelection.length === STATUS_TYPES.length ? ["all"] : newSelection;
            });
        }
    };

    // When dropdown closes, select "All" if none are selected
    React.useEffect(() => {
        if (!statusDropdownOpen && selectedStatus.length === 0) {
            setSelectedStatus(["all"]);
        }
    }, [statusDropdownOpen, selectedStatus.length]);

    // When "All" is selected, just show "All" in the select box
    const displayLabels = selectedStatus.includes("all")
        ? "All"
        : STATUS_TYPES.filter(st => selectedStatus.includes(st.value))
              .map(st => st.label)
              .join(", ");

    if (!open) return null;
    function handleClear(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
        event.stopPropagation();
        setSelectedMediaType("");
        setSelectedStatus([]);
    }
    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                zIndex: 1000
            }}
            onClick={() => onClose({ mediaType: selectedMediaType, status: selectedStatus })}
        >
            <div
                style={{
                    position: "absolute",
                    top: position.top,
                    left: position.left,
                    background: "#fff",
                    padding: 24,
                    borderRadius: 8,
                    minWidth: 300,
                    minHeight: 120,
                    boxShadow: "0 2px 16px rgba(0,0,0,0.15)"
                }}
                onClick={e => e.stopPropagation()}
            >
                <div style={{ marginBottom: 16, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>Filter Options</span>
                    <button
                        style={{
                            background: "transparent",
                            border: "none",
                            color: "#1976d2",
                            fontWeight: 500,
                            cursor: "pointer",
                            fontSize: 14,
                            padding: "2px 8px"
                        }}
                        onClick={handleClear}
                    >
                        Clear
                    </button>
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Media Type</label>
                    <select
                        value={selectedMediaType}
                        onChange={e => setSelectedMediaType(e.target.value)}
                        style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
                    >
                        <option value="">All</option>
                        {MEDIA_TYPES.map(mt => (
                            <option key={mt.value} value={mt.value}>
                                {mt.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={{ marginBottom: 16, position: "relative" }}>
                    <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Status</label>
                    <div
                        ref={statusDropdownRef}
                        style={{
                            width: "100%",
                            borderRadius: 4,
                            border: "1px solid #ccc",
                            background: "#fff",
                            cursor: "pointer",
                            minHeight: 44,
                            padding: 8
                        }}
                        onClick={() => setStatusDropdownOpen(open => !open)}
                    >
                        {selectedStatus.length === 0 ? "Select status..." : displayLabels}
                    </div>
                    {statusDropdownOpen && (
                        <div
                            style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                width: "100%",
                                background: "#fff",
                                border: "1px solid #ccc",
                                borderRadius: 4,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                zIndex: 10,
                                maxHeight: 220,
                                overflowY: "auto"
                            }}
                        >
                            {STATUS_OPTIONS.map(st => (
                                <div
                                    key={st.value}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "6px 12px",
                                        cursor: "pointer",
                                        background:
                                            selectedStatus.includes("all") && st.value !== "all"
                                                ? "#e3f2fd"
                                                : selectedStatus.includes(st.value)
                                                ? "#e3f2fd"
                                                : "#fff"
                                    }}
                                    onMouseDown={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleStatusToggle(st.value);
                                    }}
                                >
                                    <span
                                        style={{
                                            display: "inline-block",
                                            width: 18,
                                            height: 18,
                                            marginRight: 8,
                                            border: "1px solid #1976d2",
                                            borderRadius: 4,
                                            background:
                                                selectedStatus.includes("all") && st.value !== "all"
                                                    ? "#1976d2"
                                                    : selectedStatus.includes(st.value)
                                                    ? "#1976d2"
                                                    : "#fff",
                                            color: "#fff",
                                            textAlign: "center",
                                            lineHeight: "18px",
                                            fontSize: 14
                                        }}
                                    >
                                        {(selectedStatus.includes("all") && st.value !== "all") || selectedStatus.includes(st.value) ? "✓" : ""}
                                    </span>
                                    <span>{st.label}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FilterPopup;
